/**
 * LE CRON D'UNE PLANIFICATION DE FLUX, lu dans un FUSEAU.
 *
 * Cinq champs, comme partout : minute (0-59), heure (0-23), jour du mois
 * (1-31), mois (1-12), jour de semaine (0-6, dimanche = 0 ; 7 vaut aussi
 * dimanche). Chaque champ admet `*`, une liste `1,15`, un intervalle `1-5` et
 * un pas `*\/15` ou `8-18/2`. Comme le cron classique, si le jour du mois ET le
 * jour de semaine sont restreints, l'un OU l'autre suffit.
 *
 * Le calcul se fait en heure LOCALE du fuseau (`Intl`), sans librairie : on
 * avance en minutes UTC et on lit l'heure locale, donc un changement d'heure
 * ne decale rien (09:00 reste 09:00 a Luxembourg en hiver comme en ete).
 */

export interface CronLu {
  minutes: Set<number>;
  heures: Set<number>;
  jours: Set<number>;
  mois: Set<number>;
  joursSemaine: Set<number>;
  joursRestreints: boolean;
  semaineRestreinte: boolean;
}

const BORNES: ReadonlyArray<[number, number]> = [
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 7],
];

function lireChamp(texte: string, [min, max]: [number, number], nomChamp: string): Set<number> {
  const valeurs = new Set<number>();
  for (const morceau of texte.split(",")) {
    const [plage = "", pasTexte] = morceau.split("/");
    const pas = pasTexte === undefined ? 1 : Number(pasTexte);
    if (!Number.isInteger(pas) || pas < 1) throw new Error(`Cron : pas invalide dans ${nomChamp}.`);
    let debut: number;
    let fin: number;
    if (plage === "*") {
      debut = min;
      fin = max;
    } else if (plage.includes("-")) {
      const [a, b] = plage.split("-").map(Number);
      debut = a ?? NaN;
      fin = b ?? NaN;
    } else {
      debut = Number(plage);
      fin = pasTexte === undefined ? debut : max;
    }
    if (!Number.isInteger(debut) || !Number.isInteger(fin) || debut < min || fin > max || debut > fin) {
      throw new Error(`Cron : valeur hors bornes dans ${nomChamp} (${min}-${max}).`);
    }
    for (let v = debut; v <= fin; v += pas) valeurs.add(v);
  }
  return valeurs;
}

const NOMS_CHAMPS = ["minute", "heure", "jour du mois", "mois", "jour de semaine"];

/** Lit un cron a cinq champs ; leve une erreur lisible sinon. */
export function lireCron(cron: string): CronLu {
  const champs = cron.trim().split(/\s+/);
  if (champs.length !== 5) throw new Error("Cron : cinq champs attendus (minute heure jour mois jour-de-semaine).");
  const [m, h, j, mo, js] = champs.map((c, i) => lireChamp(c, BORNES[i] as [number, number], NOMS_CHAMPS[i] ?? ""));
  const joursSemaine = new Set([...(js as Set<number>)].map((v) => (v === 7 ? 0 : v)));
  return {
    minutes: m as Set<number>,
    heures: h as Set<number>,
    jours: j as Set<number>,
    mois: mo as Set<number>,
    joursSemaine,
    joursRestreints: champs[2] !== "*",
    semaineRestreinte: champs[4] !== "*",
  };
}

/** Un cron est-il valide ? */
export function cronValide(cron: string): boolean {
  try {
    lireCron(cron);
    return true;
  } catch {
    return false;
  }
}

/** Un fuseau IANA est-il connu de ce moteur JavaScript ? */
export function fuseauValide(fuseau: string): boolean {
  try {
    new Intl.DateTimeFormat("fr-FR", { timeZone: fuseau });
    return true;
  } catch {
    return false;
  }
}

const formats = new Map<string, Intl.DateTimeFormat>();
function formatDe(fuseau: string): Intl.DateTimeFormat {
  let f = formats.get(fuseau);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: fuseau, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", weekday: "short" });
    formats.set(fuseau, f);
  }
  return f;
}
const SEMAINE: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** L'heure locale d'un instant dans un fuseau. */
export function heureLocale(instant: Date, fuseau: string): { annee: number; mois: number; jour: number; heure: number; minute: number; jourSemaine: number } {
  const parts = Object.fromEntries(formatDe(fuseau).formatToParts(instant).map((p) => [p.type, p.value]));
  return {
    annee: Number(parts["year"]),
    mois: Number(parts["month"]),
    jour: Number(parts["day"]),
    heure: Number(parts["hour"]) % 24,
    minute: Number(parts["minute"]),
    jourSemaine: SEMAINE[parts["weekday"] ?? "Sun"] ?? 0,
  };
}

/**
 * Le prochain instant STRICTEMENT apres `apres` qui satisfait le cron, en
 * heure locale de `fuseau`. Cherche sur deux ans au plus ; `null` si rien
 * (un 31 fevrier).
 */
export function prochainPassageCron(cron: string, fuseau: string, apres: Date): Date | null {
  const c = lireCron(cron);
  const MINUTE = 60_000;
  let t = Math.floor(apres.getTime() / MINUTE) * MINUTE + MINUTE;
  const limite = apres.getTime() + 2 * 366 * 24 * 60 * MINUTE;
  while (t <= limite) {
    const l = heureLocale(new Date(t), fuseau);
    const jourOk = c.joursRestreints && c.semaineRestreinte ? c.jours.has(l.jour) || c.joursSemaine.has(l.jourSemaine) : c.jours.has(l.jour) && c.joursSemaine.has(l.jourSemaine);
    if (!c.mois.has(l.mois) || !jourOk) {
      // Saute au debut du jour local suivant.
      t += ((23 - l.heure) * 60 + (60 - l.minute)) * MINUTE;
      continue;
    }
    if (!c.heures.has(l.heure)) {
      t += (60 - l.minute) * MINUTE;
      continue;
    }
    if (!c.minutes.has(l.minute)) {
      t += MINUTE;
      continue;
    }
    return new Date(t);
  }
  return null;
}

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const deux = (n: number): string => String(n).padStart(2, "0");

/** Le cron en une etiquette lisible : « Tous les jours a 09:00 ». Retombe sur le cron brut. */
export function decrireCron(cron: string): string {
  const champs = cron.trim().split(/\s+/);
  if (champs.length !== 5) return cron;
  const [m, h, j, mo, js] = champs as [string, string, string, string, string];
  const simple = (v: string): boolean => /^\d+$/.test(v);
  const pasMinutes = /^\*\/(\d+)$/.exec(m);
  if (pasMinutes && h === "*" && j === "*" && mo === "*" && js === "*") return `Toutes les ${pasMinutes[1]} min`;
  if (simple(m) && h === "*" && j === "*" && mo === "*" && js === "*") return `Toutes les heures à :${deux(Number(m))}`;
  if (simple(m) && simple(h) && mo === "*") {
    const heure = `${deux(Number(h))}:${deux(Number(m))}`;
    if (j === "*" && js === "*") return `Tous les jours à ${heure}`;
    if (j === "*" && js === "1-5") return `En semaine à ${heure}`;
    if (j === "*" && /^[0-7](,[0-7])*$/.test(js)) return `Chaque ${js.split(",").map((d) => JOURS[Number(d) % 7]).join(", ")} à ${heure}`;
    if (simple(j) && js === "*") return `Le ${Number(j)} du mois à ${heure}`;
  }
  return `Cron ${cron}`;
}
